import { DatePipe, DOCUMENT, NgFor, NgIf } from '@angular/common';
import { Component, Inject, Renderer2, ViewEncapsulation } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Category } from '../../models/category';
import { CategoryApiService } from '../../services/category-api.service';
import { Deal } from '../../models/deal';
import { DealApiService } from '../../services/deal-api.service';
import { Brand } from '../../models/brand';
import { BrandApiService } from '../../services/brand-api.service';
import { Meta, Title } from '@angular/platform-browser';
import { PageMeta } from '../../models/page-meta';
import { ReplaceMetaDatePipe } from '../../pipes/replace-meta-date.pipe';
import { PagesApiService } from '../../services/pages-api.service';

@Component({
  selector: 'app-best-deals',
  imports: [NgFor, NgIf, DatePipe, RouterModule],
  templateUrl: './best-deals.component.html',
  styleUrl: './best-deals.component.css',
  encapsulation: ViewEncapsulation.None
})
export class BestDealsComponent {
  categories: Category[] = [];
  topDeals: Deal[] = [];
  topBrands: Brand[] = [];
  today: Date = new Date();
  pendingCalls: number = 0;
  isDataLoaded: boolean = false;
  pagemeta: PageMeta[] = [];

  constructor(
    private categoryApi: CategoryApiService,
    private dealsApi: DealApiService,
    private storeApi: BrandApiService,
    private siteApi: PagesApiService,
    private meta: Meta,
    private title: Title,
    private replaceMetaPipe: ReplaceMetaDatePipe,
    @Inject(DOCUMENT) private dom: Document,
    private renderer: Renderer2
  ) {
    this.loadAllData();
  }

  loadAllData() {
    this.pendingCalls = 3;
    this.getTopCategories();
    this.getTopDeals();
    this.getPageMeta();
    //this.getTopBrands();
  }

  checkIfAllDone() {
    this.pendingCalls--;
    if (this.pendingCalls === 0) {
      this.isDataLoaded = true;
    }
  }

  public loadScript(url: string) {
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = false;
    script.defer = true;
    body.appendChild(script);
  }

  getTopCategories() {
    this.categoryApi.getTopCategories(1).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.categories = data.Data;
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getTopDeals() {
    this.dealsApi.getAllDeals(0, 1, 0, 2, 2, false).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.topDeals = data.Data;
      } else {
        this.directToNotFoundPage();
      }
      this.checkIfAllDone();
    });
  }

  directToNotFoundPage() {
    window.location.href = `not-found`;
  }

  getTopBrands() {
    this.storeApi.getStores(1, 1, 10).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.topBrands = data.Data;
      } else {
      }
      this.checkIfAllDone();
    });
  }

  // directToCategoryCoupons(cat: Category) {
  //   if (cat && cat.CategoryId > 0) {
  //     window.location.href = `coupons/${cat.CategorySEOName}/`;
  //   } else {
  //     console.error('Cat ID is undefined or invalid:');
  //   }
  // }

  directToDealCodePage(deal: Deal) {
    const url = deal.DealUrl;
    window.open(url!, '_blank');
    window.location.href = `deal-code/${deal.DealId}/`;
  }

  updateMetaData(data: PageMeta) {
    const pageTitle = this.replaceMetaPipe.transform(data.PageTitle!);
    const pageDescription = this.replaceMetaPipe.transform(data.PageDescription!);
    const pageUrl = `https://www.dealszo.com/deals/`;
    // const keyWords = data.PageKeywords!;

    // Update the title and meta tags dynamically
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'title', content: pageTitle });
    this.meta.updateTag({ name: 'description', content: pageDescription });
    // this.meta.updateTag({
    //   name: 'keywords',
    //   content: keyWords,
    // });

    this.meta.updateTag({
      name: 'robots',
      content:
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    });

    // Open Graph meta tags
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'dealszo.com' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({
      property: 'og:description',
      content: pageDescription,
    });
    // this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });

    let link: HTMLLinkElement = this.dom.querySelector(
      `link[rel='canonical']`
    )!;
    let alternativeLink: HTMLLinkElement = this.dom.querySelector(
      `link[rel='alternate']`
    )!;
    if (link) {
      this.renderer.setAttribute(link, 'href', pageUrl);
    } else {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.setAttribute(link, 'href', pageUrl);
      this.renderer.appendChild(this.dom.head, link);
    }
    if (alternativeLink) {
      this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
    } else {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(alternativeLink, 'rel', 'canonical');
      this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
      this.renderer.appendChild(this.dom.head, link);
    }

    if (data.PageSchema && data.PageSchema.trim() !== '') {
      const schema = this.replaceMetaPipe.transform(data.PageSchema);
      const jsonLdScripts = this.dom.head.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      jsonLdScripts.forEach(script => script.remove());
      const temp = this.dom.createElement('div');
      temp.innerHTML = schema;
      const scripts = temp.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach((scriptTag, index) => {
        const newScript = this.dom.createElement('script');
        newScript.type = 'application/ld+json';
        newScript.id = `schema-jsonld-${index}`;
        newScript.text = scriptTag.textContent || '';
        this.dom.head.appendChild(newScript);
      });
    }
  }

  getPageMeta() {
    this.siteApi.getPagesMeta().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.pagemeta = data.Data;
        this.updateMetaData(this.pagemeta.find((p) => p.PageName == 'deals')!);
      } else {
      }
      this.checkIfAllDone();
    });
  }

}
